import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useFormik } from 'formik';
import { Button } from '@/components/Button/Button';
import { useState } from 'react';
import { CheckPinCodeModal } from '@/bundle/shared/components/CheckPinCodeModal/CheckPinCodeModal';
import { ORGANIZATION_USER_RESTRICTED_ACTION_ERROR_MESSAGE, PIN_TOKEN_EVENT_SCOPE } from '@/const/shared';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { getExcludedResponseError, hasResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { OrgAdminRoleType } from '@/const/user';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { CannotModifyUserErrorModal } from '@/bundle/shared/components/modals/CannotModifyUserErrorModal';
import { getOpcoOrganizationUsersPath } from '@/bundle/_Opco/ui/OrganizationDetails/path/path';
import { FormikPanelRadioGroup } from '@/components/form/fields/FormikPanelRadioGroup/FormikPanelRadioGroup';
import { SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS } from '@/const/formConfigs';
import { EditAdminRoleModal } from '@/bundle/shared/components/modals/EditAdminRoleModal';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { editOrganizationUserRole } from '@/bundle/_Opco/ui/OrganizationDetails/Details/api/api';

type EditUserRoleFormType = {
  role: OrgAdminRoleType;
  onSuccess: () => void;
  onClose: () => void;
};

export const EditUserRoleForm = ({ role, onSuccess, onClose }: EditUserRoleFormType) => {
  const navigate = useNavigate();

  const [isPinCodeOpen, setIsPinCodeOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isUpdateRoleModalOpen, setIsUpdateRoleModalOpen] = useState(false);
  const { organizationId, userId } = useParams<{ organizationId: string; userId: string }>();

  const closePinCode = () => {
    setIsPinCodeOpen(false);
  };

  const formik = useFormik({
    initialValues: { role },
    enableReinitialize: false,
    onSubmit: (values) => {
      const payload = getChangedFormValues(values, formik.initialValues);

      if (!payload) {
        onClose();

        return;
      }

      setIsUpdateRoleModalOpen(true);
    },
  });

  const { data, mutate, isPending } = useMutation({
    mutationKey: ['edit_role'],
    mutationFn: (pinCode: string) => {
      return editOrganizationUserRole(organizationId, userId, formik.values?.role, pinCode);
    },

    onSuccess(response) {
      if (response?.error) {
        const hasError = hasResponseError(response?.error, ORGANIZATION_USER_RESTRICTED_ACTION_ERROR_MESSAGE);

        if (hasError) {
          return setIsErrorModalOpen(true);
        }

        return;
      }

      onSuccess();
    },
  });

  const checkPinCodeSuccess = (pinToken: string) => {
    mutate(pinToken);
    closePinCode();
  };

  const openPinCodeModal = () => {
    setIsUpdateRoleModalOpen(false);
    setIsPinCodeOpen(true);
  };

  const navigateToOrganizations = () => {
    navigate(getOpcoOrganizationUsersPath(organizationId));
  };

  const formattedError = getExcludedResponseError(data?.error, ORGANIZATION_USER_RESTRICTED_ACTION_ERROR_MESSAGE);
  const userRole = formik.values.role;

  return (
    <>
      {formattedError && (
        <Notification variant='error' marginBottom='24px'>
          {formattedError}
        </Notification>
      )}
      <FormikForm value={formik}>
        <FormikPanelRadioGroup name='role' label='Role*' options={SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS} />

        <ButtonGroup marginTop='14px'>
          <Button variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' isLoading={isPending}>
            Save
          </Button>
        </ButtonGroup>
      </FormikForm>
      {isPinCodeOpen && (
        <CheckPinCodeModal
          isOpen={isPinCodeOpen}
          onClose={closePinCode}
          onSuccess={checkPinCodeSuccess}
          eventScope={PIN_TOKEN_EVENT_SCOPE.EDIT_ORG_ADMIN}
        />
      )}

      <CannotModifyUserErrorModal
        isOpen={isErrorModalOpen}
        header='Role cannot be changed'
        subHeader='Please assign another user of this organization as a Master Admin in order to change the role of this user.'
        onClose={() => setIsErrorModalOpen(false)}
        onBack={navigateToOrganizations}
      />

      <EditAdminRoleModal
        role={userRole}
        isOpen={isUpdateRoleModalOpen}
        onConfirm={openPinCodeModal}
        onClose={() => setIsUpdateRoleModalOpen(false)}
      />
    </>
  );
};
